import React from "react"
import { Form, Col, Image, Spinner } from "react-bootstrap"
import { Typeahead } from "react-bootstrap-typeahead"
import { BehaviorSubject, concat, of, Subject, Subscription } from "rxjs"
import { filter, map, switchMap } from "rxjs/operators"
import SelectServiceTogglePanel from "./SelectServiceTogglePanel"
import { ServiceDataModel, EmptyServiceData } from "../OrderModels"
import dotImage from "../../../assets/images/dot-img.png"
import SelectService, { SelectServiceProps, SelectServiceState } from "./SelectService"

import TestToBePerformGetObservable from "../../../api/LabService/TestToBePerformGet"
import LabAccountsGetObservable from "../../../api/LabService/LabAccountsGet"
import LabAccount from "../../../api/LabService/LabServiceModel/LabAccount"
import TestToBePerform from "../../../api/LabService/LabServiceModel/TestToBePerform"
import { ServiceTypeCode } from "../../../api/Administration/Model/ServiceType"
import Service, { ServiceCode, IsServiceActive, IsServicePOCT, IsServiceDrugDOT, IsServiceDrugNonDOT, ServiceByCode, IsServiceCodePOCT, IsServiceFLEX } from "../../../api/Administration/Model/Service"
import TestToBePerformWithCompanyName from "../../../api/LabService/LabServiceModel/TestToBePerformWithCompanyName"
import SelectSampleTestType from "./SelectSampleTestType"
import { SampleType } from "../../../api/OrderRequests/OrderRequestsModel/SampleType"
import SelectSpecimenCollection from "./SelectSpecimenCollection"
import SelectObserved from "./SelectObserved"
import RegulatoryModeControl from "../RegulatoryModeControl"

export interface SelectDrugTestProps extends SelectServiceProps {
  company?: number
  location?: number
  canAddPOCT: boolean
  canAddFLEX: boolean
  isObservedMandatory: boolean
}

interface SelectDrugTestState extends SelectServiceState {
  labAccountsLoading: boolean
  testsLoading: boolean
  labAccounts: LabAccount[]
  availableTests: TestToBePerform[]
  tests: TestToBePerform[]
  dotTestCount: number
  ndotTestCount: number
  poctTestCount: number
  flexTestCount: number
  labAccountCompanyName?: string
}

export default class SelectDrugTest extends SelectService<SelectDrugTestProps, SelectDrugTestState> {
  defaultService: ServiceDataModel = {
    ...EmptyServiceData,
    serviceTypeCode: ServiceTypeCode.Drug,
    serviceCode: ServiceCode.None,
    observedCollection: false,
  }
  state: SelectDrugTestState = {
    ...this.defaultService,
    labAccountsLoading: false,
    testsLoading: false,
    labAccounts: [],
    availableTests: [],
    tests: [],
    dotTestCount: 0,
    ndotTestCount: 0,
    poctTestCount: 0,
    flexTestCount: 0,
    isDOT: this.props.isDot,
    reqSpecimenCollection: this.props.isDot ? "split" : "single",
  }

  subscriptions = new Subscription()
  company$ = new BehaviorSubject<number>(0)
  labAccount$ = new Subject<LabAccount>()
  service$ = new Subject<TestToBePerform>()

  componentDidMount() {
    this.subscriptions.add(
      this.company$
        .pipe(
          switchMap((company: number) =>
            !company
              ? of({ isLoading: false })
              : concat(
                  of({ isLoading: true }),
                  LabAccountsGetObservable({
                    SearchFor: "",
                    SearchBy: "Account Name",
                    CompanyID: company,
                    RegionID: 0,
                    LocationID: this.props.location || 0,
                    DOT: !!this.state.isDOT,
                    IsMarketPlace: true,
                  }).pipe(map((data) => ({ isLoading: false, result: data })))
                )
          )
        )
        .subscribe((data: { isLoading: boolean; result?: LabAccount[] }) => {
          this.setState({
            labAccountsLoading: data.isLoading,
            labAccounts: data.result || [],
          })
          const selectedLabAccount = this.props.labAccountNumber
            ? data.result?.find((item) => item.LabAccount === this.props.labAccountNumber)
            : undefined
          this.labAccount$.next(selectedLabAccount)
        })
    )

    this.subscriptions.add(
      this.labAccount$.subscribe((selected) =>
        this.setState({
          labAccount: selected?.LabCode || "",
          labAccountObj: selected,
          availableTests: [],
          tests: [],
          testToPerform: "",
          testToPerformObj: undefined,
          labAccountCompanyName: undefined,
        })
      )
    )
    this.subscriptions.add(
      this.labAccount$
        .pipe(
          filter((selected) => !!selected),
          switchMap((selected: LabAccount) =>
            concat(
              of({ isLoading: true }),
              TestToBePerformGetObservable({
                LabAccountNumber: selected.LabAccount,
                VendorID: selected.LabClientMemberId,
                IsDOT: !!this.state.isDOT,
                IsPoct: IsServiceCodePOCT(this.state.serviceCode),
                IsMarketPlace: true,
              }).pipe(map((data) => ({ isLoading: false, result: data })))
            )
          )
        )
        .subscribe((data: { isLoading: boolean; result?: TestToBePerformWithCompanyName }) => {
          const availableTests = data.result?.Panels || []
          const companyName = data.result?.CompanyName
          const labAccount = this.state.labAccountObj

          if (!!companyName && companyName.length > 0 && labAccount) {
            const newLabAccount = {
              ...labAccount,
              Description: labAccount.Description.replace("Undefined", companyName),
            }
            if (newLabAccount.Description !== labAccount.Description) {
              const index = this.state.labAccounts.indexOf(labAccount)
              const newLabAccountsArray: LabAccount[] = [
                ...this.state.labAccounts.slice(0, index),
                newLabAccount,
                ...this.state.labAccounts.slice(index + 1),
              ]

              this.setState({ labAccountObj: newLabAccount, labAccounts: newLabAccountsArray })
            }
          }

          this.setState(
            {
              testsLoading: data.isLoading,
              availableTests: availableTests,
              labAccountCompanyName: data.result?.CompanyName,
              tests: this.filterTests(availableTests),
              testToPerform: "",
              testToPerformObj: undefined,
              dotTestCount: this.dotTestsCount(availableTests),
              ndotTestCount: this.ndotTestsCount(availableTests),
              poctTestCount: this.poctTestsCount(availableTests),
              flexTestCount: this.flexTestsCount(availableTests),
            },
            () => {
              const selectedService = availableTests.find((item) => item.PanelID === this.props.panelId)
              if (!selectedService) {
                const { dotTestCount, ndotTestCount, poctTestCount, flexTestCount } = this.state
                if (flexTestCount > 0) {
                  this.onChangeTestType(ServiceCode.FLEX)
                } else {
                  if (dotTestCount > 0 && ndotTestCount === 0 && poctTestCount === 0)
                    this.onChangeTestType(ServiceCode.DrugDOT)
                  if (dotTestCount === 0 && ndotTestCount > 0 && poctTestCount === 0)
                    this.onChangeTestType(ServiceCode.Drug)
                  if (dotTestCount === 0 && ndotTestCount === 0 && poctTestCount > 0)
                    this.onChangeTestType(ServiceCode.POCT)
                }
              } else {
                let serviceCode = ServiceCode.Drug
                if (selectedService?.DOT) serviceCode = ServiceCode.DrugDOT
                if (selectedService?.POCT) serviceCode = ServiceCode.POCT
                if (selectedService?.FLEX) serviceCode = ServiceCode.FLEX
                this.onChangeTestType(serviceCode)
              }
            }
          )
        })
    )

    this.subscriptions.add(
      this.service$.subscribe((selected) => {
        const selectedSampleType = selected?.SampleTypeCode as SampleType

        this.setState((prev) => ({
          testToPerform: selected?.Code || "",
          testToPerformObj: selected,
          selectedTestType: selectedSampleType,
          tests: this.filterTests(prev.availableTests, undefined,selectedSampleType),
        }))
      })
    )

    this.props.company && this.company$.next(this.props.company)
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  componentDidUpdate(prevProps: SelectDrugTestProps) {
    if (
      this.props.company &&
      (this.props.company !== prevProps.company || (this.props.location && this.props.location !== prevProps.location))
    ) {
      this.company$.next(this.props.company)
    }
  }

  setInitialState = () => this.setState({ ...this.defaultService, labAccountCompanyName: undefined })

  filterTests = (
    tests: TestToBePerform[],
    service: Service | undefined = undefined, 
    sampleType: SampleType | undefined = undefined
  ) => {
    const { orderableServices } = this.props
    const { serviceCode } = this.state 
    
    const selectedService = service || ServiceByCode(orderableServices, serviceCode) 
    const isPoct = IsServicePOCT(selectedService)
    const isFlex = IsServiceFLEX(selectedService)
    const isDOT = isPoct ? undefined : selectedService.DOT
    
    let filteredTests = tests
    if (isPoct) {
      filteredTests = tests.filter((test) => test.POCT)
    } else if (isFlex) {
      filteredTests = tests.filter((test) => test.FLEX)
    } else if (isDOT === true) {
      filteredTests = tests.filter((test) => test.DOT)
    } else if (isDOT === false) {
      filteredTests = tests.filter((test) => test.NDOT)
    }
    if (sampleType) {
      filteredTests = filteredTests.filter((t) => t.SampleTypeCode === sampleType)
    }
    return filteredTests
  }

  dotTestsCount = (tests: TestToBePerform[]) => tests.filter((t) => t.DOT).length

  ndotTestsCount = (tests: TestToBePerform[]) => tests.filter((t) => t.NDOT).length

  poctTestsCount = (tests: TestToBePerform[]) => tests.filter((t) => t.POCT).length

  flexTestsCount = (tests: TestToBePerform[]) => tests.filter((t) => t.FLEX).length

  setDefaultDrugSampleType = () => {
    const { tests } = this.state

    //const tests = this.filterTests(availableTests)
    let sampleType: SampleType | undefined = undefined
    
    if (tests.every((test) => test.SampleTypeCode === SampleType.UR)) {
      sampleType = SampleType.UR
    } else if (tests.every((test) => test.SampleTypeCode === SampleType.SLD)) {
      sampleType = SampleType.SLD
    }

    if (sampleType) {
      this.setState((prev) => ({
        ...prev,
        tests: this.filterTests(prev.availableTests, undefined, sampleType),
        selectedTestType: sampleType
      })) 
    }
  }

  onChangeLab = (lab: LabAccount) => {
    this.setState({ ...this.defaultService }, () => this.labAccount$.next(lab))
  }

  onChangeTestType = (value: string) => {
    const orderableServices = this.props.orderableServices
    const serviceCode = value as ServiceCode
    const service = ServiceByCode(orderableServices, serviceCode)
    const isPOCT = IsServicePOCT(service)
    const isDOT = service.DOT
    const req_specimen_collection = isPOCT ? "single" : isDOT ? "split" : "single"
    const selectedTest = this.props.panelId
      ? this.state.availableTests.find((item) => item.PanelID === this.props.panelId)
      : undefined

    this.setState((prev) => ({
      serviceCode: serviceCode,
      isDOT: isDOT,
      isMarketplace: service.Marketplace,
      reqSpecimenCollection: req_specimen_collection,
      tests: this.filterTests(prev.availableTests, service),
      testToPerform: selectedTest?.Description || "",
      testToPerformObj: selectedTest,
      selectedTestType: undefined
    }), this.setDefaultDrugSampleType)
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value
    this.setState({ ...{}, [e.target.name]: value })
  }

  onChangeSampleTestsType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedTestType = e.target.value as SampleType

    this.setState((prev) => ({
      ...prev,
      observedCollection: selectedTestType === SampleType.UR ? prev.observedCollection : false,
      selectedTestType: selectedTestType,
      tests: this.filterTests(prev.availableTests, undefined,selectedTestType),
      testToPerform: "",
      testToPerformObj: undefined,
    }))
  }

  onServiceAdd = () => {
    const observedCollection = this.state.observedCollection || (this.props.isObservedMandatory && this.state.isDOT)
    this.props.onAdd({ ...this.state, observedCollection: observedCollection } as ServiceDataModel)
    this.setInitialState()
  }

  onPanelClose = () => {
    // Clear all entered values
    this.setInitialState()
  }

  isValid = (data: ServiceDataModel): boolean => {
    if (this.isAllServicesDisabled || this.props.denyAddService) {
      return false
    }

    return (IsServiceCodePOCT(data.serviceCode) || typeof data.isDOT === "boolean") // POCT or DOT or Non-DOT type is set
      && !!data.testToPerform && data.testToPerform.length > 0 // testToPerform is set
      && !!data.labAccount && data.labAccount.length > 0 // labAccount is set
      && (data.isDOT !== true || this.state.isValidMode)
  }

  hasAvailableTests = () => {
    const { availableTests, labAccountObj } = this.state

    return labAccountObj && availableTests.length > 0
  }

  showSelectSampleTestType = () => {
    const { serviceCode, isDOT } = this.state

    return (
      this.hasAvailableTests() &&
      !isDOT &&
      ![ServiceCode.None, ServiceCode.POCT, ServiceCode.FLEX].includes(serviceCode)
    )
  }

  showSpecimenCollection = () => {
    const { selectedTestType } = this.state
    const showSelectSampleTestType = this.showSelectSampleTestType()

    return (
      (!showSelectSampleTestType && this.hasAvailableTests()) ||
      (showSelectSampleTestType && selectedTestType !== undefined)
    )
  }

  showObserved = () => {
    const { selectedTestType } = this.state
    const showSelectSampleTestType = this.showSelectSampleTestType()

    return (
      (!showSelectSampleTestType && this.hasAvailableTests()) ||
      (showSelectSampleTestType && selectedTestType === SampleType.UR)
    )
  }

  showDOTRelatedControls = () => {
    const { isDOT } = this.state

    return this.hasAvailableTests() && isDOT === true
  }

  render() {
    const { disabled, isObservedMandatory } = this.props
    const {
      labAccounts,
      labAccountObj,
      labAccountsLoading,
      availableTests,
      tests,
      testToPerformObj,
      testsLoading,
      labAccountCompanyName,
      isDOT,
      reqSpecimenCollection,
      observedCollection,
      selectedTestType,
    } = this.state

    const observedCheckedDisabled = isObservedMandatory && isDOT

    return (
      <SelectServiceTogglePanel
        title="DRUG TEST"
        data={this.state as ServiceDataModel}
        collapsibleId="DrugTestCollapsePanel"
        disabled={disabled}
        open={this.props.open}
        onSubmit={this.onServiceAdd}
        onValidate={this.isValid}
        onClose={this.onPanelClose}
      >
        <Form.Row>
          <Col xs="12">
            <Form.Group>
              <Form.Label className="font-weight-bold required">LAB ACCOUNT</Form.Label>
              <Typeahead
                id="labAccountsTypeahead"
                labelKey="Description"
                filterBy={(option, props) => {
                  return (
                    props.selected.length > 0 ||
                    option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                  )
                }}
                onChange={(selected: LabAccount[]) => this.onChangeLab(selected[0])}
                options={labAccounts}
                isInvalid={!labAccountObj}
                disabled={!!this.props.labAccountNumber}
                placeholder="Enter the Lab Account name"
                selected={labAccountObj ? [labAccountObj] : []}
              />
            </Form.Group>
          </Col>
        </Form.Row>
        {labAccountCompanyName && (
          <Form.Row>
            <Col className="font-weight-bold form-group">{labAccountCompanyName}</Col>
          </Form.Row>
        )}
        {labAccountObj && availableTests.length === 0 && !labAccountsLoading && !testsLoading && (
          <Form.Row>
            <Col className="error-message">No services found for lab account</Col>
          </Form.Row>
        )}
        {testsLoading && (
          <Form.Row>
            <Col className="text-center">
              <Spinner animation="border" variant="secondary" />
            </Col>
          </Form.Row>
        )}
        {this.hasAvailableTests() && (
          <Form.Row>
            <Col xs="11" md="11" sm="11">
              <Form.Group className={!this.state.serviceCode ? "invalid-section-border p-2" : "section-border p-2"}>
                {this.getServicesComponents()}
              </Form.Group>
            </Col>
            <Col xs="1" md="1" sm="1" hidden={isDOT !== true}>
              <Image src={dotImage} />
            </Col>
          </Form.Row>
        )}
        {this.showSelectSampleTestType() && (
          <Form.Row>
            <Col xs="12">
              <SelectSampleTestType
                selectedValue={selectedTestType}
                availableTests={this.filterTests(availableTests)}
                disabled={!!testToPerformObj}
                onChange={this.onChangeSampleTestsType}
              />
            </Col>
          </Form.Row>
        )}
        {this.showSpecimenCollection() && (
          <Form.Row>
            <Col xs="12">
              <SelectSpecimenCollection
                selectedValue={reqSpecimenCollection}
                disabled={isDOT === true}
                onChange={this.onChange}
              />
            </Col>
          </Form.Row>
        )}
        {this.showObserved() && (
          <Form.Row>
            <Col xs="12">
              <SelectObserved
                checked={observedCheckedDisabled || observedCollection}
                disabled={observedCheckedDisabled}
                onChange={this.onChange}
              />
            </Col>
          </Form.Row>
        )}
        {this.showDOTRelatedControls() && <RegulatoryModeControl
          defaults={this.props.defaults}
          regulatoryModeID={this.props.regulatoryModeID}
          employeeCategoryID={this.props.employeeCategoryID}
          onChange={(isValid, mode, cat) => this.setState({ isValidMode: isValid, regulatoryModeObj: mode, categoryObj: cat })}
        />}
        {this.hasAvailableTests() && (
          <Form.Row>
            <Col xs="12">
              <Form.Group>
                <Form.Label className="font-weight-bold required">TEST TO BE PERFORMED</Form.Label>
                <Typeahead
                  id="drugTestToPerformTypeahead"
                  labelKey="Description"
                  filterBy={(option, props) => {
                    return (
                      props.selected.length > 0 ||
                      option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                    )
                  }}
                  onChange={(selected) => this.service$.next(selected[0])}
                  isInvalid={!testToPerformObj}
                  options={tests}
                  disabled={testsLoading || !!this.props.panelId}
                  placeholder="Enter the Test name"
                  selected={testToPerformObj ? [testToPerformObj as TestToBePerform] : []}
                />
              </Form.Group>
            </Col>
          </Form.Row>
        )}
      </SelectServiceTogglePanel>
    )
  }

  getServicesComponents() {
    const { canAddDOT, canAddNonDOT, canAddPOCT, canAddFLEX } = this.props
    const { serviceCode, dotTestCount, ndotTestCount, poctTestCount, flexTestCount } = this.state
    const selectedTest = this.state.testToPerformObj as TestToBePerform

    const orderableServices = this.props.orderableServices;
    const orderableAccountServices = this.props.orderableAccountServices
    const marketplaceServicesCount = this.props.marketplaceServicesCount;
    const notMarketplaceServicesCount = this.props.notMarketplaceServicesCount;
    const canAddMarketplace = notMarketplaceServicesCount === 0;
    const canAddNotMarketplace = marketplaceServicesCount === 0;

    let servicesComponents = [];
    this.isAllServicesDisabled = true;
    for (let i = 0; i < orderableServices.length; i++) {
      const orderableService = orderableServices[i];

      const is_poct = IsServicePOCT(orderableService)
      const is_flex = IsServiceFLEX(orderableService)

      //if ((is_poct && flexTestCount > 0) || (is_flex && flexTestCount === 0)) { continue }

      const serviceActive = IsServiceActive(orderableService, orderableAccountServices);
      const is_dot = IsServiceDrugDOT(orderableService)
      const is_non_dot = IsServiceDrugNonDOT(orderableService)

      let item_type = ""
      if (is_poct) {
        item_type = "poct"
      } else if (is_flex) {
        item_type = "flex"
      } else {
        item_type = orderableService.DOT ? "regulated" : "non-regulated"
      }

      const item_id = `${item_type}-drug-test-radio`
      const item_value = orderableService.FFServiceCode;

      let item_disabled = !serviceActive
        || (is_dot && (!canAddDOT || (dotTestCount === 0)))
        || (is_non_dot && (!canAddNonDOT || (ndotTestCount === 0)))
        || (is_poct && (!canAddPOCT || (poctTestCount === 0)))
        || (orderableService.Marketplace && !canAddMarketplace)
        || (!orderableService.Marketplace && !canAddNotMarketplace)
        || (is_flex && (!canAddFLEX || (flexTestCount === 0)))
        || (!is_flex && flexTestCount > 0)

      if (!item_disabled) {
        this.isAllServicesDisabled = false;
      }

      let item_checked = (serviceCode === orderableService.FFServiceCode && !item_disabled)

      if (!!selectedTest && !!this.props.labAccountNumber) {
        item_disabled = !item_checked
      }

      const serviceComponent = <Form.Check label={orderableService.ServiceDescription}
        id={item_id}
        key={item_id}
        type="radio"
        name="testType"
        className="font-weight-bold abc-radio"
        disabled={item_disabled}
        value={item_value}
        onChange={(e) => this.onChangeTestType(e.target.value)}
        checked={item_checked} />
      servicesComponents.push(serviceComponent)
    }

    return <Form.Group className="mb-0">{servicesComponents}</Form.Group>
  }
}
